import { client, injectAuthToken, injectReCaptchaResponse } from '@core/api/API'

/**
 * @typedef {{
 *   username: string,
 *   email: string,
 *   memberSince: string,
 *   avatar: string|null,
 *   locationCountryId: number|null,
 *   locationStateId: number|null,
 *   locationCityId: number|null
 * }} ApiUserProfile
 */
/**
 * @typedef {{
 *   timestamp: number;
 *   condition: string;
 *   icon: string;
 *   temperature: {
 *     value: number;
 *     feelsLike: number;
 *     min: number;
 *     max: number;
 *   };
 *   pressure: number;
 *   humidity: number;
 *   clouds: number;
 *   visibility: number;
 *   precipitation?: number;
 *   wind: {
 *     speed: number;
 *     deg: number;
 *     gust?: number;
 *   };
 *   rain?: number;
 *   snow?: number;
 * }} WeatherData
 */

export default {
  /**
   * @param {string} token
   * @returns {Promise<ApiUserProfile>}
   */
  async getAuthorizedUser(token) {
    const response = await client.get('/user', {
      headers: injectAuthToken({}, token),
    })

    return response.data
  },

  /**
   *
   * @param {{
   *   email: string,
   *   password: string,
   * }} data
   * @param {string} recaptchaToken
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  async authorize(data, recaptchaToken) {
    return await client.post(`/user/login`, {
      email: data.email,
      password: data.password,
    }, {
      headers: injectReCaptchaResponse({}, recaptchaToken),
    })
  },

  /**
   * @param {string} token
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  async logout(token) {
    return await client.post(`/user/logout`, {}, {
      headers: injectAuthToken({}, token),
    })
  },

  /**
   * @param {{
   *   email: string,
   *   username: string,
   *   password: string,
   *   emailVerificationCode: string,
   * }} data
   * @param {string} recaptchaToken
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  async register(data, recaptchaToken) {
    return await client.post(`/user/register`, {
      email: data.email,
      username: data.username,
      password: data.password,
      emailVerificationCode: data.emailVerificationCode,
    }, {
      headers: injectReCaptchaResponse({}, recaptchaToken),
    })
  },

  /**
   * @param {{
   *   email: string,
   *   lang?: string
   * }} data
   * @param recaptchaToken
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  async sendVerificationEmail(data, recaptchaToken) {
    return await client.post(`/user/verify-email`, data, {
      headers: injectReCaptchaResponse({}, recaptchaToken),
    })
  },

  /**
   * @param {string} email
   * @param {string} recaptchaToken
   * @returns {Promise<void>}
   */
  async forgotPassword(email, recaptchaToken) {
    await client.post(`/user/forgot-password`, {
      email,
    }, {
      headers: injectReCaptchaResponse({}, recaptchaToken),
    })
  },

  /**
   * @param {{
   *  code: string,
   *  password: string,
   * }} data
   * @param {string} recaptchaToken
   * @returns {Promise<void>}
   */
  async resetPassword(data, recaptchaToken) {
    await client.post(`/user/reset-password`, {
      code: data.code,
      password: data.password,
    }, {
      headers: injectReCaptchaResponse({}, recaptchaToken),
    })
  },

  /**
   * @param {{
   *   oldPassword: string,
   *   newPassword: string,
   * }} data
   * @param {string} token
   * @returns {Promise<void>}
   */
  async changePassword(data, token) {
    await client.post(`/user/change-password`, data, {
      headers: injectAuthToken({}, token),
    })
  },

  /**
   * @param {File} file
   * @param {token} token
   * @returns {Promise<string>}
   */
  async uploadAvatar(file, token) {
    const formData = new FormData()

    formData.append('avatar', file)

    const response = await client.post(`/user/profile/avatar`, formData, {
      headers: injectAuthToken({
        'Content-Type': 'multipart/form-data',
      }, token),
    })

    return response.data.avatarUrl
  },

  /**
   * @param {string} token
   * @returns {Promise<void>}
   */
  async deleteAvatar(token) {
    await client.delete(`/user/profile/avatar`, {
      headers: injectAuthToken({}, token),
    })
  },

  /**
   * @param {{
   *  username?: string,
   * }} data
   * @param {string} token
   * @returns {Promise<ApiUserProfile>}
   */
  async updateProfile(data, token) {
    const response = await client.patch(`/user/profile`, data, {
      headers: injectAuthToken({}, token),
    })


    return response.data
  },

  /**
   * @param {{
   *  locationCountryId: number,
   *  locationStateId: number,
   *  locationCityId: number,
   * }} data
   * @param {string} token
   * @returns {Promise<ApiUserProfile>}
   */
  async updateLocation(data, token) {
    const response = await client.patch(`/user/location`, data, {
      headers: injectAuthToken({}, token),
    })


    return response.data
  },

  /**
   * @param {string} token
   * @returns {Promise<{
   *   current: WeatherData,
   *   forecast: WeatherData[]
   * }>}
   */
  async getLocationWeatherForecast(token) {
    const response = await client.get(`/user/location/forecast`, {
      headers: injectAuthToken({}, token),
    })

    return response.data
  },

  /**
   * @param {{
   *   lang?: string
   * }} data
   * @param {string} token
   * @returns {Promise<void>}
   */
  async requestDeletion(data, token) {
    await client.post('/user/request-deletion', data, {
      headers: injectAuthToken({}, token),
    })
  },

  /**
   * @param {{
   *   code: string
   * }} data
   * @param {string} token
   * @returns {Promise<void>}
   */
  async confirmDeletion(data, token) {
    await client.post(`/user/confirm-deletion`, data, {
      headers: injectAuthToken({}, token),
    })
  },
}
